import {$, $$, createElement, empty, getParentElementByTagName, getParentElementWithAttribute, html_chargement_en_cours} from "./Dom.ts";
import {MicroModalConfig} from "micromodal";
// @ts-ignore
import {Modal} from "./CustomMicromodal";
import {$get} from "./Ajax.ts";
import {initTooltips} from "./Tooltip.ts";
import {createListe} from "./List.ts";
import {animateLoadingButton, fadeIn} from "./Animate.ts";
import {refreshPage} from "./Utils.ts";


export type IModalOptions = {
    title?: HTMLElement | string
    content?: HTMLElement
    actions?: HTMLElement[]
    small?: boolean
    title_in_header?: boolean
}

const defaultOptions = {
    actions: [], small: false, title_in_header: false,
}

const defaultMicroModalOptions: MicroModalConfig = {
    disableScroll: true,
}

const generateModalTemplate = (id: string) => {
    const modal = createElement('div', ['modal', 'micromodal-slide'])
    modal.id = id
    modal.setAttribute('aria-hidden', 'true')
    modal.innerHTML = `
    <div class="modal__overlay" tabindex="-1" data-micromodal-close>
        <div id="${id}-modal-container" class="modal__container" role="dialog" aria-modal="true" aria-labelledby="${id}-title">
            <header id="${id}-modal-header" class="modal__header">
                <button class="fr-btn--close fr-btn" title="Fermer la fenêtre modale" data-micromodal-close>Fermer</button>
            </header>
            <div id="${id}-modal-content-content" class="modal-content-content">
                <h2 id="${id}-modal__title" class="modal__title"></h2>
                <div id="${id}-modal__content" class="modal__content"></div>
                <footer id="${id}-modal-footer" class="modal__footer">
                    <ul id="${id}-actions" class="fr-btns-group fr-btns-group--right fr-btns-group--inline fr-mx-0"></ul>
                </footer>
            </div>
        </div>
    </div>
`
    return modal
}

/**
 * fonction qui créer l'ensemble du dom pour la modal automatiquement et affiche la modale
 * @param {string} modalId
 * @param {IModalOptions} modaleOptions
 * @param {MicroModalConfig} libOptions
 */
export const generateModale = (modalId: string, modaleOptions: IModalOptions, libOptions?: MicroModalConfig) => {
    const options = {...defaultOptions, ...modaleOptions}
    const defaultModalConfig = {...defaultMicroModalOptions, ...libOptions}
    if (!$(`#${modalId}`)) {
        document.body.appendChild(generateModalTemplate(modalId))
    }
    const modale = $<HTMLDialogElement>(`#${modalId}`)
    if (modale) {
        const title = $<HTMLElement>(`#${modalId}-modal__title`)!
        const header = $<HTMLElement>(`#${modalId}-modal-header`)!
        const content = $<HTMLElement>(`#${modalId}-modal__content`)!
        const actions = $<HTMLElement>(`#${modalId}-actions`)!
        if (options.title) {
            if (options.title_in_header === true) {
                // Créer un nouvel élément h2
                const titleElement = document.createElement('h2');
                titleElement.classList.add('modal__title'); // Ajouter la classe modal__title
                //@ts-ignore
                titleElement.innerText = options.title; // Ajouter le texte du titre
                header.prepend(titleElement);
            } else if (typeof options.title === 'string') {
                title.innerText = options.title
            } else {
                title.appendChild(options.title)
            }
        }
        if (options.content) content.appendChild(options.content)
        options.actions?.forEach(action => {
            const li = createElement('li')
            li.appendChild(action)
            actions.appendChild(li)
        })
        Modal.show(modalId, {
            onClose: () => {
                modale?.remove()
            }, ...defaultModalConfig,
        })
        return modale
    }
    return null
}


/**
 * fonction qui utilise une modale existante dans le dom pour l'afficher
 * @param {string} modalId
 * @param {IModalOptions} modaleOptions
 */
export const createModale = (modalId: string, modaleOptions?: IModalOptions): HTMLElement | null => {
    const options = {...defaultOptions, ...modaleOptions}
    const modale = $<HTMLDialogElement>(`#${modalId}`)
    if (modale) {
        const title = $<HTMLElement>(`#${modalId}-modal__title`)!
        const content = $<HTMLElement>(`#${modalId}-modal__content`)!
        const actions = $<HTMLElement>(`#${modalId}-actions`)!
        if (options.title) {
            if (typeof options.title === 'string') {
                title.innerText = options.title
            } else {
                title.appendChild(options.title)
            }
        }
        if (options.content) content.appendChild(options.content)
        options.actions?.forEach(action => {
            const li = createElement('li')
            li.appendChild(action)
            actions.appendChild(li)
        })
        return modale
    }
    return null
}

export const initModals = (libOptions?: MicroModalConfig) => {
    Modal.init({
        awaitCloseAnimation: true, awaitOpenAnimation: true, debugMode: false, disableScroll: true, ...libOptions,
    })
}


export const initComplexeModals = (options: MicroModalConfig) => {
    Modal.init(options)
}

/**
 * Affiche une modale
 * @param {string} modalId
 * @param {MicroModalConfig} libOptions
 */
export const showModal = (modalId: string, libOptions?: MicroModalConfig | boolean) => {
    if (libOptions) {
        Modal.show(modalId, libOptions)
    } else {
        window.dsfr($(modalId))?.modal?.disclose();
    }
}

/**
 * masque une modale
 * @param {string} modalId
 */
export const hideModal = (modalId: string) => {
    Modal.close(modalId)
    window.dsfr($(modalId))?.modal?.conceal();
}

class HistoryModal {
    private template = `
    <div class="fr-container fr-container--fluid fr-container-md">
        <div class="fr-grid-row fr-grid-row--center">
            <div class="fr-col-12 fr-col-md-8 fr-col-lg-6">
                <div class="fr-modal__body">
                    <div class="fr-modal__header">
                        <button class="fr-btn--close fr-btn" title="Fermer la fenêtre modale" aria-controls="history-modal">Fermer</button>
                    </div>
                    <div class="fr-modal__content">
                        <h1 id="fr-modal-title-history-modal" class="fr-modal__title">Historique</h1>
                        <div id="history-modal-content"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    `

    private rendered = false

    private readonly dialog: HTMLDialogElement

    constructor() {
        this.dialog = createElement('dialog', ['fr-modal'])
        this.dialog.id = 'history-modal'
        this.dialog.setAttribute('aria-labelledby', 'fr-modal-title-history-modal')
        this.dialog.setAttribute('role', 'dialog')
        this.dialog.innerHTML = this.template
    }

    private render() {
        if (!this.rendered) {
            document.body.appendChild(this.dialog)
            this.rendered = true
        }
    }

    update(url?: string) {
        if (url) {
            this.render()
            const body = $('#history-modal-content', this.dialog)
            requestAnimationFrame(() => {
                if (body) {
                    empty(body)
                    body.appendChild(html_chargement_en_cours())
                    window.dsfr(this.dialog).modal?.disclose()
                    $get(url).then(response => {
                        body.innerHTML = response.body
                        if (response.body.indexOf("historique-list") !== -1) {
                            // gestion de la modal de l'admin des service qui a une list.js
                            createListe('historique-list', {valueNames: ['name', 'champ', {attr: 'data-timestamp', name: 'date'}]}, {valueName: 'date', options: {order: "desc"}},)
                        }
                        initTooltips(body)
                    })
                }
            })
        }
    }
}

export const historyModal = new HistoryModal()

export const initHistoryModal = (container: Document | HTMLElement | string | null) => {
    if (container === null) return
    let parent: HTMLElement | Document | null
    if (typeof container === 'string') parent = $<HTMLElement>(container)
    else parent = container

    if (parent) {
        $$('.history_link', parent).forEach(el => el.addEventListener('click', (event: DOMEvent<HTMLElement>) => {
            const target = getParentElementByTagName(event.target, 'button') as HTMLAnchorElement
            if (target) {
                event.stopPropagation()
                event.preventDefault()
                historyModal.update(target.dataset.url)
            }
        }))
    }
}

export function registerModalListener(parent: HTMLElement | Document = document) {
    $$('[data-modal]', parent).forEach((element) => {
        element.addEventListener('click', handleModalButtonClick)
    })
}

function handleModalButtonClick(event: DOMEvent<HTMLElement>) {
    const button = getParentElementWithAttribute(event.target, 'data-modal')
    const modalName = button?.dataset?.modal
    if (modalName) {
        switch (modalName) {
            case 'utilisateur_enlever_role_administrateur_structure':
                generateModalForAdminRoleStructure(button, "enlever")
                break;
            case 'utilisateur_ajouter_role_administrateur_structure':
                generateModalForAdminRoleStructure(button)
                break;
        }
    }
}

function generateModalForAdminRoleStructure(element: HTMLElement, action = "ajouter") {
    const url = element.dataset.url
    const utilisateur = element.dataset.user_id
    const organisation = element.dataset.orga_id

    const annuleBtn = createElement('button', ['fr-btn', 'fr-btn--secondary'])
    annuleBtn.innerText = "Annuler"
    annuleBtn.setAttribute("data-micromodal-close", "")

    const enregistreBtn = createElement('button', ['fr-btn', 'del_admin'])

    let enregistreBtnText = "Supprimer le rôle administrateur"
    if (action === "ajouter") {
        enregistreBtn.classList.add('ajout_admin', 'btn_action_edit_groupe_admin')
        enregistreBtn.classList.remove('del_admin')
        enregistreBtnText = "Ajouter au rôle administrateur"
    }
    enregistreBtn.innerText = enregistreBtnText

    const modalId = `modal_utilisaeur_role_administrateur_structure`

    const modal = generateModale(modalId, {
        title: action == "ajouter" ? "Ajouter au rôle administrateur" : "Supprimer du rôle administrateur",
        content: createElement("div"),
        actions: [annuleBtn, enregistreBtn,]
    }, {
        onClose: (modal) => modal?.remove()
    })

    enregistreBtn.addEventListener("click", () => {
        if (url) {
            const modalContent = $(`#${modalId}-modal__content`, modal!)!
            const {resetBtn} = animateLoadingButton(enregistreBtn)!
            modalContent.innerHTML = ""
            $get(url, {
                user_id: utilisateur, orga_id: organisation, type: action === "ajouter" ? 'ajout' : 'supp',
            }).then(({body}) => {
                if (body === 'ok') {
                    refreshPage()
                } else {
                    const erreurElement = $('#changer_admin_error')
                    if (erreurElement) {
                        hideModal(modalId)
                        erreurElement.innerHTML = body
                        fadeIn(erreurElement)
                    } else {
                        modalContent.innerHTML = `<div class="fr-alert fr-alert--error"><p>${body}</p></div>`
                    }
                }
            }).finally(() => {
                resetBtn()
            })
        }
    })
}

export async function initAjaxModale(modalId: string, modalOptions: IModalOptions, url: string, params: any = {}): Promise<HTMLDialogElement> {
    return new Promise((resolve, reject) => {
        const modale = generateModale(modalId, {
            ...modalOptions,
            content: html_chargement_en_cours(),
        })

        if(modale) {
            $get(url, params).then(data => {
                const modalContent = $(`#${modalId}-modal__content`)
                if (modalContent) modalContent.innerHTML = data.body
                resolve(modale)
            }).catch(reject)
        } else reject(new Error(`Erreur de création de la modale ${modalId}`))
    })
}